/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import i18n from '@shared/i18n';

/** Severity buckets used to decide whether an error surfaces to the user. */
export type ErrorSeverity = 'critical' | 'handled' | 'silent';

/** Discriminator describing the kind of error, used to pick wording. */
export type ErrorKind = 'network' | 'timeout' | 'maintenance' | 'server' | 'auth' | 'antibot' | 'generic';

/** Localized title/message i18n keys (with defaults) per error kind. */
interface ErrorCopy {
  titleKey: string;
  titleDefault: string;
  messageKey: string;
  messageDefault: string;
}

const ERROR_COPY: Record<ErrorKind, ErrorCopy> = {
  network: {
    titleKey: 'messages.error.networkTitle',
    titleDefault: 'Connection Error',
    messageKey: 'messages.error.networkMessage',
    messageDefault: 'Please check your internet connection and try again.',
  },
  timeout: {
    titleKey: 'messages.error.timeoutTitle',
    titleDefault: 'Request Timed Out',
    messageKey: 'messages.error.timeoutMessage',
    messageDefault: 'The request took too long to respond. Please try again.',
  },
  maintenance: {
    titleKey: 'messages.error.maintenanceTitle',
    titleDefault: 'Service Unavailable',
    messageKey: 'messages.error.maintenanceMessage',
    messageDefault: "We're performing maintenance. Please try again shortly.",
  },
  server: {
    titleKey: 'messages.error.serverTitle',
    titleDefault: 'Server Error',
    messageKey: 'messages.error.serverMessage',
    messageDefault: 'An unexpected error occurred. Please try again later.',
  },
  auth: {
    titleKey: 'messages.error.sessionTitle',
    titleDefault: 'Session Expired',
    messageKey: 'messages.error.sessionExpired',
    messageDefault: 'Your session has expired. Please log in again.',
  },
  antibot: {
    titleKey: 'messages.error.securityTitle',
    titleDefault: 'Security Error',
    messageKey: 'messages.error.antiBotUnavailable',
    messageDefault: 'Security verification failed. Please reload the page.',
  },
  generic: {
    titleKey: 'messages.error.genericTitle',
    titleDefault: 'Error',
    messageKey: 'messages.error.generic',
    messageDefault: 'Something went wrong. Please try again.',
  },
};

const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred.';

/**
 * Extracts the RFC 7807 `title` from an ApiError body if present.
 * Returns undefined when the error is not an ApiError or has no domain title.
 */
function isApiErrorWithBody(error: unknown): error is { name: string; body?: { title?: string } } {
  return !!error && typeof error === 'object' && (error as any).name === 'ApiError';
}

/**
 * Extracts the domain-specific error title from an ApiError response body.
 *
 * @param error The thrown value to inspect.
 * @returns The title string, or `undefined` when absent or non-ApiError.
 */
function extractDomainTitle(error: unknown): string | undefined {
  if (!isApiErrorWithBody(error)) return undefined;
  const title = error.body?.title;
  return title && typeof title === 'string' ? title : undefined;
}

/**
 * Reads the resolved HTTP status from an error-like object.
 *
 * @param e Error-like object carrying `status` or `response.status`.
 * @returns The HTTP status if present, otherwise `undefined`.
 */
function statusOf(e: { status?: number; response?: { status?: number } }): number | undefined {
  return e.status ?? e.response?.status;
}

/**
 * Detects a request-timeout failure: the connection was fine but the request
 * took too long to respond. Distinct from {@link isNetworkError}, which means
 * there was no connection at all.
 *
 * @param e The error to inspect.
 * @returns `true` for timeout failures.
 */
function isTimeoutError(e: any): boolean {
  if (e.code === 'ECONNABORTED') return true;
  return Boolean(e.message?.includes('timeout'));
}

/**
 * Detects network/fetch failures by error code or message.
 *
 * @param e The error to inspect.
 * @returns `true` for network-level failures.
 */
function isNetworkError(e: any): boolean {
  if (e.code === 'ERR_NETWORK') return true;
  return Boolean(e.message?.includes('Network Error'));
}

/**
 * Detects a 503 Service Unavailable, e.g. planned maintenance or the backend
 * being temporarily down. Distinct from other 5xx errors, which are
 * unexpected failures rather than a known, temporary state.
 *
 * @param e The error to inspect.
 * @returns `true` when the status is 503.
 */
function isMaintenanceError(e: any): boolean {
  return statusOf(e) === 503;
}

/**
 * Detects a server-side (5xx) error from the resolved status.
 *
 * @param e The error to inspect.
 * @returns `true` when the status is in the 500–599 range.
 */
function isServerError(e: any): boolean {
  const status = statusOf(e);
  return typeof status === 'number' && status >= 500 && status <= 599;
}

/**
 * Detects an expired-session error: a 401 whose message hints at expiry.
 *
 * @param e The error to inspect.
 * @returns `true` for a 401 with an "expired"/"session" message.
 */
function isAuthExpiredError(e: any): boolean {
  if (statusOf(e) !== 401) return false;
  const msg = (e.message || '').toLowerCase();
  return msg.includes('expired') || msg.includes('session');
}

/**
 * Detects an anti-bot step-up challenge (HTTP 428 or a matching title).
 *
 * @param e The error to inspect.
 * @returns `true` when the backend requires an anti-bot challenge.
 */
function isAntiBotChallenge(e: any): boolean {
  if (statusOf(e) === 428) return true;
  const title = e.response?.data?.title ?? e.body?.title ?? e.title;
  return title === 'AntiBotChallengeRequired';
}

/**
 * Classifies an unknown error into a single {@link ErrorKind} discriminator.
 * The first matching predicate wins; anything unmatched is `generic`.
 *
 * @param error The thrown value to classify.
 * @returns The error kind used to select wording.
 */
const KIND_PREDICATES: Array<[ErrorKind, (e: any) => boolean]> = [
  ['timeout', isTimeoutError],
  ['network', isNetworkError],
  ['antibot', (e) => isAntiBotChallenge(e) || e.name === 'AntiBotUnavailableError'],
  ['maintenance', isMaintenanceError],
  ['server', isServerError],
  ['auth', isAuthExpiredError],
];

export function classifyKind(error: unknown): ErrorKind {
  if (!error || typeof error !== 'object') return 'generic';
  const match = KIND_PREDICATES.find(([, pred]) => pred(error));
  return match ? match[0] : 'generic';
}

/**
 * Maps an error to its severity. Network, server, auth, and anti-bot errors are
 * `critical` (always surfaced); everything else is `handled`.
 *
 * @param error The thrown value to classify.
 * @returns The severity bucket.
 */
export function classifyError(error: unknown): ErrorSeverity {
  return classifyKind(error) === 'generic' ? 'handled' : 'critical';
}

/**
 * Resolves the localized, user-facing title for an error.
 * When the error is an ApiError with an RFC 7807 domain `title`, maps it to
 * `errors.<title>` i18n key. Falls back to kind-based classification otherwise.
 *
 * @param error The thrown value to describe.
 * @returns The translated title string.
 */
export function formatErrorTitle(error: unknown): string {
  const domainTitle = extractDomainTitle(error);
  if (domainTitle) {
    const key = `errors.${domainTitle}`;
    const translated = i18n.t(key, { ns: 'common', defaultValue: '' });
    if (translated && translated !== key) return translated;
    return i18n.t('errors.unknown', { ns: 'common', defaultValue: 'Error' });
  }
  const copy = ERROR_COPY[classifyKind(error)];
  return i18n.t(copy.titleKey, { ns: 'common', defaultValue: copy.titleDefault });
}

/**
 * Resolves the localized, user-facing message for an error.
 *
 * A domain ApiError NEVER renders its `detail` field (which can leak upstream
 * internals like "Payment gateway error: status=500"). Instead, when the
 * domain `title` has a curated message under `errors.messages.<title>`, that
 * meaningful sentence is shown; otherwise it falls back to the generic
 * "unexpected error" line. This is what turns a `SubscriptionUpstreamError`
 * 502 from "Ocorreu um erro inesperado." into an actionable instruction to
 * retry the payment, without exposing the gateway status.
 *
 * @param error The thrown value to describe.
 * @returns The translated message string.
 */
export function formatErrorMessage(error: unknown): string {
  const domainTitle = extractDomainTitle(error);
  if (domainTitle) {
    const key = `errors.messages.${domainTitle}`;
    const curated = i18n.t(key, { ns: 'common', defaultValue: '' });
    if (curated && curated !== key) return curated;
    return i18n.t('errors.unknownMessage', {
      ns: 'common',
      defaultValue: GENERIC_ERROR_MESSAGE,
    });
  }
  const copy = ERROR_COPY[classifyKind(error)];
  return i18n.t(copy.messageKey, { ns: 'common', defaultValue: copy.messageDefault });
}
